import { useMemo } from "react"
import {
  Button,
  ButtonProps,
  Flex,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react"

import checkmarkWhite from "#/assets/checkmark-white.svg"
import {
  RootState,
  selectActiveToken,
  selectCanUsePortal,
  selectDepositAmount,
  selectHasApprovalError,
  selectHasLoadedWalletBalances,
  selectTokenApprovalStatus,
  selectTokenBalance,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import TransactionProgressLoader from "#/shared/components/TransactionProgressLoader"
import TransactionRejected, {
  TransactionRejectedProps,
} from "#/shared/components/TransactionRejected"
import { useTokenApproval } from "#/shared/hooks"
import { floatToBigInt } from "#/shared/utils"

type DepositApproveButtonProps = ButtonProps & {
  defaultButtonText?: string
  /**
   * Props applied to button in loading state
   */
  loaderButtonProps?: ButtonProps
  rejectedStateProps?: Omit<TransactionRejectedProps, "onClick">
  allowApproveRetry?: boolean
  blockOnError?: boolean
  errorStateBelowButton?: boolean
  /**
   * Allows us to control current balance
   *
   * Main use case now is to control how much stBTC has been minted
   * against deposit selected in the carousel
   */
  customBalance?: string
}

export default function DepositApproveButton({
  defaultButtonText = "Approve Asset",
  loaderButtonProps,
  rejectedStateProps,
  allowApproveRetry = true,
  blockOnError = true,
  errorStateBelowButton = true,
  customBalance,
  ...props
}: DepositApproveButtonProps) {
  const depositAmount = useDappSelector(selectDepositAmount)
  const tokenApprovalStatus = useDappSelector(selectTokenApprovalStatus)

  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)

  const activeToken = useDappSelector(selectActiveToken)
  const hasError = useDappSelector(selectHasApprovalError)

  const { approveToken } = useTokenApproval()

  const canUsePortal = useDappSelector(selectCanUsePortal)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const tokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, activeToken),
  )

  const balance = useMemo(
    () =>
      customBalance
        ? floatToBigInt(customBalance, tokenDecimals)
        : tokenBalance,
    [customBalance, tokenBalance, tokenDecimals],
  )

  const amount = useMemo(
    () => floatToBigInt(depositAmount, tokenDecimals),
    [depositAmount, tokenDecimals],
  )

  const disabled = useMemo(
    () => !canUsePortal || tokenApprovalStatus === "approved" || amount === 0n,
    [amount, canUsePortal, tokenApprovalStatus],
  )

  const shouldBlockButton = useMemo(
    () =>
      disabled ||
      tokenApprovalStatus === "pending" ||
      amount > balance ||
      !hasLoadedWalletBalances ||
      (blockOnError && hasError),
    [
      amount,
      disabled,
      tokenApprovalStatus,
      balance,
      blockOnError,
      hasError,
      hasLoadedWalletBalances,
    ],
  )

  const approveButton =
    tokenApprovalStatus === "approved" && amount > 0n ? (
      <Flex gap={4}>
        <Image src={checkmarkWhite} />
        <Text>Approved</Text>
      </Flex>
    ) : (
      defaultButtonText
    )

  const buttonProps =
    tokenApprovalStatus === "pending"
      ? { ...props, ...loaderButtonProps }
      : props

  const rejectedComponent = useMemo(
    () =>
      allowApproveRetry ? (
        <TransactionRejected
          {...rejectedStateProps}
          onClick={() => approveToken()}
        />
      ) : (
        <TransactionRejected {...rejectedStateProps} />
      ),
    [allowApproveRetry, approveToken, rejectedStateProps],
  )

  const buttonVariant = useMemo(
    () => (shouldBlockButton ? "disabled" : "primaryBlack"),
    [shouldBlockButton],
  )

  return (
    <VStack flexDir={errorStateBelowButton ? "column" : "column-reverse"}>
      <Button
        variant={buttonVariant}
        color="primary.white.100"
        size={{ base: "medium", xl: "large" }}
        h="64px"
        onClick={approveToken}
        isDisabled={shouldBlockButton}
        {...buttonProps}
      >
        {tokenApprovalStatus === "pending" ? (
          <TransactionProgressLoader text="Waiting for approval..." />
        ) : (
          approveButton
        )}
      </Button>
      {hasError && tokenApprovalStatus === "non-approved" && rejectedComponent}
    </VStack>
  )
}
